/**
 * processCalendarData — computes derived fields from raw calendar data.
 *
 * INPUT JSON STRUCTURE:
 * {
 *   "YYYY-MM-DD": {
 *     ipData: {
 *       periodDay: boolean,      // User marked as period day
 *       periodStart: boolean,    // Computed: first periodDay after 7+ day gap
 *       cd: number,              // Computed: cycle day (1-28+)
 *       peak: boolean,           // Computed: highest test value >= 0.65 in cycle
 *       predictedOvulation: boolean, // Computed: CD14 prediction
 *       predictedPeriod: boolean,    // Computed: CD28 prediction
 *       peakPredicted: boolean,  // Computed: predicted peak day
 *       inseminationDay: boolean, // User marked as insemination day
 *       periodEnd: boolean,      // Computed: last period day before gap
 *     },
 *     ovulationTests: [{        // Ovulation tests for this day
 *       id: string,
 *       photo_url: string,       // Public URL to test image
 *       value: number,           // AI-predicted LH value (e.g., 1.28)
 *       level: string,           // Test level (e.g., "high", "low")
 *       peak: boolean,          // User marked as peak
 *       taken_at: string,       // ISO timestamp
 *     }],
 *     maxTestValue: number,     // Computed: max value from ovulationTests
 *     events: [{                // Calendar events
 *       id: string,
 *       title: string,
 *       date: string,
 *       user: boolean,          // true = user's event, false = shared event
 *     }],
 *     note: string,             // User note for this day
 *     sharedOvulationPredictions: [{ // For donor calendar: shared ovulation predictions
 *       userId: string,
 *       date: string,
 *     }],
 *   }
 * }
 *
 * OUTPUT JSON STRUCTURE:
 * Same as input, with computed fields added:
 * - ipData.periodStart: boolean (computed)
 * - ipData.cd: number (computed, 1-28+)
 * - ipData.peak: boolean (computed, highest test value >= 0.65 in cycle)
 * - maxTestValue: number (computed from ovulationTests)
 * - All days in range filled with cd values (including gaps)
 *
 * WHAT IT DOES:
 * 1. Identifies period start dates based on periodDay flags
 *    - First periodDay ever → periodStart
 *    - Any periodDay >7 days after previous periodStart → new periodStart
 *    - PeriodDays within 7 days of previous periodStart → not periodStart (handles spotting)
 *
 * 2. Calculates cycle days (cd) for all days
 *    - cd = 1 on each periodStart
 *    - Increments each day until next periodStart
 *    - Days before first periodStart have no cd
 *    - Fills in ALL days from earliest to latest (including gaps)
 *    - Extends to 64 days after most recent periodStart
 *
 * 3. Creates empty day entries for gaps to ensure continuous cd values
 *
 * 4. Detects peak ovulation days
 *    - For each cycle (between periodStart dates), find the day with highest test value >= 0.65
 *    - Mark that day's ipData.peak = true
 *    - If no tests >= 0.65, no peak marked for that cycle
 *
 * WHAT WE WANT IT TO DO (for prediction algorithms):
 * - Provide accurate cycle day calculations for ovulation prediction
 * - Identify true period starts (not spotting) using 7-day gap rule
 * - Calculate average cycle length from periodStart dates
 * - Track ovulation test values by cycle day for AI-based prediction
 * - Enable historical analysis: when did peaks occur relative to periodStart?
 * - Support future predictions by extending calendar data forward
 *
 * PREDICTION ALGORITHM INPUTS AVAILABLE:
 * - periodStart dates → calculate average cycle length
 * - ipData.peak → actual peak days detected from test values >= 0.65
 * - ovulationTests[].value → find peak values (>=0.65) and their cycle days
 * - cd values → correlate test values with cycle position
 * - Historical data → identify patterns in user's cycle
 */
export function processCalendarData(data) {
  // Sort all day keys chronologically
  const sortedDays = Object.keys(data).sort();

  // Deep-clone so we don't mutate the input
  const result = {};
  for (const day of sortedDays) {
    result[day] = JSON.parse(JSON.stringify(data[day]));
  }

  if (sortedDays.length === 0) return result;

  // Pass 1: mark periodStart
  // Rule: the first periodDay that is 8+ days after the previous periodStart
  // is a new periodStart. Any periodDay within 7 days of a previous
  // periodStart is just another period day (handles gap days / spotting).
  let lastPeriodStartDate = null;

  for (let i = 0; i < sortedDays.length; i++) {
    const day = sortedDays[i];
    const mark = result[day];
    if (!mark.ipData?.periodDay) continue;

    if (!lastPeriodStartDate) {
      // First periodDay ever → it's a periodStart
      mark.ipData.periodStart = true;
      lastPeriodStartDate = day;
      continue;
    }

    // Calculate actual calendar days since last periodStart
    const lastStart = new Date(lastPeriodStartDate + 'T00:00');
    const thisDay = new Date(day + 'T00:00');
    const diffDays = Math.round((thisDay.getTime() - lastStart.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays > 7) {
      // More than a week since last periodStart → new cycle
      mark.ipData.periodStart = true;
      lastPeriodStartDate = day;
    }
  }

  // Pass 2: fill in ALL days from the earliest to the latest, assigning cd
  // First, find all periodStart dates
  const periodStarts = sortedDays.filter(d => result[d].ipData?.periodStart);

  if (periodStarts.length === 0) return result;

  // Determine the full date range to fill
  const earliest = sortedDays[0];
  const latest = sortedDays[sortedDays.length - 1];

  // Extend range to 64 days after the most recent period start
  const mostRecentPeriodStart = periodStarts[periodStarts.length - 1];
  const mostRecentStart = new Date(mostRecentPeriodStart + 'T00:00');
  const extendedEnd = new Date(mostRecentStart);
  extendedEnd.setDate(extendedEnd.getDate() + 64);

  // Use the later of: existing latest date or 64 days after most recent period start
  const end = new Date(latest + 'T00:00') > extendedEnd ? new Date(latest + 'T00:00') : extendedEnd;

  // Walk every single day in the range and assign cd
  const cursor = new Date(earliest + 'T00:00');
  let currentCycleStart = null;

  while (cursor <= end) {
    const key = cursor.toISOString().slice(0, 10);

    // If this day is a periodStart, start a new cycle
    if (result[key]?.ipData?.periodStart) {
      currentCycleStart = key;
    }

    if (currentCycleStart) {
      // Calculate cd from the current cycle start
      const startDate = new Date(currentCycleStart + 'T00:00');
      const diffMs = cursor.getTime() - startDate.getTime();
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      const cd = diffDays + 1;

      // Create entry if it doesn't exist, or merge cd into existing entry
      if (!result[key]) {
        result[key] = { ipData: { cd } };
      } else {
        if (!result[key].ipData) {
          result[key].ipData = {};
        }
        result[key].ipData.cd = cd;
      }
    }

    // Move to next day
    cursor.setDate(cursor.getDate() + 1);
  }

  // Pass 3: calculate maxTestValue for each day with ovulation tests
  for (const day of sortedDays) {
    if (result[day].ovulationTests && result[day].ovulationTests.length > 0) {
      const values = result[day].ovulationTests
        .map(test => test.value)
        .filter(v => v !== null && v !== undefined);
      if (values.length > 0) {
        result[day].maxTestValue = Math.max(...values);
      }
    }
  }

  // Pass 4: detect peak ovulation days (actual ovulation)
  // For each cycle (between periodStart dates), find the day with highest test value >= 0.65
  for (let i = 0; i < periodStarts.length; i++) {
    const cycleStart = periodStarts[i];
    const cycleEnd = periodStarts[i + 1] || sortedDays[sortedDays.length - 1];

    // Collect all tests in this cycle with their day and value
    const cycleTests = [];
    for (const day of sortedDays) {
      if (day >= cycleStart && day <= cycleEnd && result[day].ovulationTests) {
        result[day].ovulationTests.forEach(test => {
          if (test.value !== null && test.value !== undefined) {
            cycleTests.push({ day, value: test.value });
          }
        });
      }
    }

    // Filter for values >= 0.65 and find the highest
    const peakTests = cycleTests.filter(t => t.value >= 0.65);
    if (peakTests.length > 0) {
      const peak = peakTests.reduce((max, t) => t.value > max.value ? t : max);
      if (!result[peak.day].ipData) {
        result[peak.day].ipData = {};
      }
      result[peak.day].ipData.peak = true;
      result[peak.day].ipData.ovulationActual = true;
    }
  }

  return result;
}

/**
 * addPredictions — adds predicted ovulation and period flags to calendar data.
 *
 * INPUT: Processed calendar data with cd (cycle day) values from processCalendarData
 * OUTPUT: Same data with predictedOvulation and predictedPeriod flags added
 *
 * RULES:
 * - Predict ovulation on cycle day 14 (CD14)
 * - Predict period start on cycle day 28 (CD28)
 * - When CD28 is reached, reset CD to 1 for the next day (new cycle)
 * - Extend predictions until endDate (or 64 days if not provided)
 *
 * PREDICTION LOGIC:
 * This is a SIMPLE prediction algorithm that:
 * 1. Uses fixed ovulation day (CD14) - NOT based on historical data
 * 2. Uses fixed cycle length (28 days) - NOT based on user's actual cycle
 * 3. Resets cycle at CD28 regardless of actual period start
 *
 * WHAT WE WANT (future improvement):
 * - Use average cycle length from user's historical periodStart dates
 * - Use average ovulation day from user's historical peak test cycle days
 * - Factor in ovulation test values (peaks >= 1.5) to predict ovulation
 * - Handle irregular cycles with bounds checking
 * - Fallback to defaults if insufficient data
 *
 * ALGORITHM AVAILABLE IN REFERENCE:
 * See Reference/kindred-connect/src/lib/ovulation.functions.ts for a more
 * sophisticated algorithm that:
 * - Calculates average cycle length from periodStart dates
 * - Finds historical peak tests (value >= 1.5) and their cycle days
 * - Uses average peak cycle day for ovulation prediction
 * - Falls back to luteal phase method (avg cycle - 14)
 * - Falls back to CD14 if no data
 *
 * @param {object} data - Processed calendar data with cd values
 * @param {string} endDate - Optional end date in YYYY-MM-DD format
 */
export function addPredictions(data, endDate = null) {
  const result = {};
  for (const day in data) {
    result[day] = { ...data[day] };
    if (result[day].ipData?.cd === 14) {
      if (!result[day].ipData) {
        result[day].ipData = {};
      }
      result[day].ipData.predictedOvulation = true;
    }
  }

  // Find the last period start to extend predictions from
  const sortedDays = Object.keys(data).sort();
  let lastPeriodStart = null;
  for (const day of sortedDays) {
    if (data[day].ipData?.periodStart) {
      lastPeriodStart = day;
    }
  }

  if (!lastPeriodStart) return result;

  // Extend predictions until endDate or 64 days default
  const cursor = new Date(lastPeriodStart + 'T00:00');
  const end = endDate ? new Date(endDate + 'T00:00') : new Date(cursor);
  if (!endDate) {
    end.setDate(end.getDate() + 64);
  }

  let currentCycleStart = lastPeriodStart;

  while (cursor <= end) {
    const key = cursor.toISOString().slice(0, 10);

    // Calculate cycle day
    const startDate = new Date(currentCycleStart + 'T00:00');
    const diffMs = cursor.getTime() - startDate.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
    const cd = diffDays + 1;

    // Create or update the day
    if (!result[key]) {
      result[key] = { ipData: {} };
    } else if (!result[key].ipData) {
      result[key].ipData = {};
    }

    // Set cycle day
    result[key].ipData.cd = cd;

    // Mark predicted ovulation (CD14)
    if (cd === 14) {
      result[key].ipData.predictedOvulation = true;
      result[key].ipData.ovulationPrediction = true;
    }

    // Mark predicted period start (CD28) and reset cycle
    if (cd === 28) {
      result[key].ipData.predictedPeriod = true;
      currentCycleStart = key;
    }

    cursor.setDate(cursor.getDate() + 1);
  }

  return result;
}
